import { FC, memo, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  useTheme,
  Switch,
  FormControlLabel,
  Tooltip,
  Fab,
  Menu,
} from '@mui/material';
import { Add, Remove, CloudUpload, Edit, Check, Close, OpenInNew, Settings, Toc, ExpandMore, ExpandLess } from '@mui/icons-material';
import { uploadImage } from '../../../../../../apis/cdn.apis';
import { ColorPicker } from '../../../../../../components/ColorPicker';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';
import { AppContext } from '../../../../../../context';
import { client } from '../../../../../../App';
import { GET_PORTFOLIO_UPLOAD } from '../../../../../../queries/widgets';
import { isUploadWithinLimit, uploadLimitExceededMessage, UPLOAD_MAX_FILE_MB } from '../../../../../../utils/uploadLimits';
import {
  SQUAD_FONDSDATEN_KEYS,
  SQUAD_SINGLE_LINE_FONDSDATEN_KEYS,
  IMAGE_SLOT_LABELS,
  SQUAD_WEITERE_DATEN_KEYS,
  SQUAD_FONDSPARTNER_KEYS,
  SECTION_HEADER_DEFAULTS,
  SQUAD_COLORS,
} from '../templateConstants';
import {
  AdvisorsEditor,
  StringListEditor,
  parseMultilineList,
  joinMultilineList,
} from '../PdfConfigEditors';
import { SquadRichTextEditor } from '../SquadRichTextEditor';
import { squadSidebarFootnoteKey, type SquadSidebarFootnotePage } from '../squadFootnotes';
import {
  CIXON_CD,
  SQUAD_FORM_GROUPS,
  SQUAD_FORM_RAIL,
  squadActiveFormGroup,
  squadFormGroupId,
  squadRailItemForGroup,
  squadRailPlacement,
  squadSpyLine,
  type SquadFormGroupKey,
} from '../squadFormNav';
import {
  setSquadLabelOverride,
  squadLabeledKeys,
  type SquadLabelKey,
  type SquadLabelOverrides,
} from '../squadLabels';
import { SQUAD_IMAGE_FORMATS, squadImageFormatHint } from '../squadImageFormats';
import {
  estimateSquadPage3ImageArea,
  resolveSquadPage3Layout,
  squadPage3OptimalSizeText,
  SQUAD_PAGE3_IMAGE_HINT,
  SQUAD_PAGE3_LEGACY_IMAGE_KEYS,
} from '../squadPage3Image';
import { SquadPage3ImageFitStatus } from '../SquadPage3ImageFitStatus';
import { useSquadTitleFontSize } from '../squadTitleFit';
import { TableConfigurationDialog, TableData, LegacyTableData } from '../../../widgets';
import {
  configWrapperStyles,
  configFieldStyles,
} from '../../../widgets/TableConfigurationDialog/styles';
import { normalizeKalenderjahrData } from '../kalenderjahr';
import { normalizePerformanceRiskTable } from '../performanceRiskTable';
import { DEFAULT_ROLLING_PERFORMANCE } from '../rollingPerformance';
import { syncSquadFundIsin } from '../squadFundIsin';
import {
  isSquadDynamicGebuehrKey,
  makeSquadGebuehrDynamic,
  SQUAD_DYNAMIC_GEBUEHREN_KEYS,
} from '../squadGebuehrenDynamic';

import {
  MIFID_OVERLAY_AREA_LABELS,
  normalizeMifidOverlay,
  SquadMifidOverlayAreaKey,
} from '../squadMifidOverlay';
import {
  mapPortfolioUploadToSquadPage2,
  parseCustomPortfolioIsins,
  parsePortfolioThemeData,
  parsePortfolioWidgetsData,
  resolveSquadPortfolioChartColors,
  suggestSquadFsHeaderPalette,
  SQUAD_FS_HEADER_SECONDARY,
} from '../squadCustomPortfolio';
import { buildSquadPortfolioSections } from '../squadPortfolioSections';
import {
  fetchSquadSrriwert,
  resolveSquadFundIsin,
  SQUAD_SRRIWERT_CELL_CONFIG,
} from '../squadSrriwert';
import { normalizeSquadAdvisors } from '../squadAdvisors';
import {
  defaultDynamicConfigForKey,
  isSquadDynamicCell,
  isSquadDynamicFondsdatenKey,
  type SquadDynamicCell,
  type SquadDynamicFondsdatenKey,
} from '../squadFondsdatenDynamic';
import {
  PDF_CONFIG_PARENT_DEBOUNCE_MS,
  scrollElementIntoScrollableColumn,
} from '../../../../debugMonthlyCommentary';
import {
  expandSquadSectionHeaders,
  isDynamicSectionHeaderKey,
  mergeSquadSectionHeaders,
  resolveSquadHeaderAsOf,
  resolveSquadSectionHeaderText,
  type SquadSectionHeaderKey,
  type SquadSectionHeaderValue,
  type SquadSectionHeaders,
} from '../sectionHeaderPlaceholders';

/** Gebühren rows that can come from the data (S1, 09.10.2026). */
const SQUAD_GEBUEHREN_DYNAMIC_ROWS = {
  keys: SQUAD_DYNAMIC_GEBUEHREN_KEYS,
  defaultFor: (key: string) => {
    if (!isSquadDynamicGebuehrKey(key)) throw new Error(`No dynamic Gebühren row ${key}`);
    return makeSquadGebuehrDynamic(key);
  },
};

type SectionHeaders = SquadSectionHeaders;

interface PdfConfigProps {
  data: any;
  onChange: (data: any) => void;
  onRegisterFlush?: (flush: () => void) => void;
}

/** True once parent document payload has been applied (not empty / SRRI-only mount state). */
function isSquadConfigHydrated(d: any): boolean {
  if (!d || typeof d !== 'object') return false;
  return Boolean(
    d.title ||
      d.historyIsin ||
      d.fondsdaten?.isin ||
      d.anlagestrategie ||
      d.monatskommentar ||
      (Array.isArray(d.assetAllocation) && d.assetAllocation.length > 0) ||
      (d.performanceRiskTable && typeof d.performanceRiskTable === 'object'),
  );
}

const ImageUploadField: FC<{
  label: string;
  value?: string;
  onChange: (url: string) => void;
  inputId?: string;
  helperText?: string;
  previewSx?: Record<string, unknown>;
}> = ({ label, value, onChange, inputId, helperText, previewSx }) => {
  const id = inputId || `upload-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {helperText}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Max. {UPLOAD_MAX_FILE_MB} MB
      </Typography>
      {value ? (
        <Box
          component="img"
          src={value}
          alt={label}
          sx={{ maxWidth: '100%', maxHeight: 80, mb: 1, display: 'block', ...previewSx }}
        />
      ) : previewSx ? (
        <Box
          sx={{
            mb: 1,
            border: '1px dashed #d1d5db',
            ...previewSx,
          }}
        />
      ) : null}
      <input
        id={id}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (!isUploadWithinLimit(file)) {
            window.alert(uploadLimitExceededMessage(file.size));
            return;
          }
          try {
            const res: any = await uploadImage(file);
            // CDN upload returns { status, fileName } (S3 key or full URL)
            let imageUrl =
              res?.data?.url ||
              res?.url ||
              (res?.status && res?.fileName ? res.fileName : '') ||
              '';
            if (imageUrl && !/^https?:\/\//i.test(imageUrl)) {
              imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${imageUrl}`;
            }
            if (imageUrl) onChange(imageUrl);
            else window.alert('Upload failed. Please try again.');
          } catch (err) {
            console.error(err);
            // http.ts throws `{ msg }` (not AxiosError); also surface size-limit JSON.
            const msg =
              (err as any)?.response?.data?.error ||
              (err as any)?.msg ||
              (err as any)?.message ||
              'Upload failed. Please try again.';
            window.alert(String(msg));
          }
        }}
      />
      <label htmlFor={id}>
        <Button component="span" size="small" variant="outlined" startIcon={<CloudUpload />}>
          {value ? 'Replace' : 'Upload'}
        </Button>
      </label>
      {value ? (
        <Button size="small" color="inherit" sx={{ ml: 1 }} onClick={() => onChange('')}>
          Clear
        </Button>
      ) : null}
    </Box>
  );
};

/**
 * Own wording for the row labels of a table (Fondsdaten, Gebühren) — collapsed by default,
 * open as soon as the document has an override. See squadLabels.
 */
const LabelOverridesEditor: FC<{
  keys: SquadLabelKey[];
  value: SquadLabelOverrides | undefined;
  onChange: (next: SquadLabelOverrides) => void;
}> = ({ keys, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const hasOverrides = Object.keys(value || {}).length > 0;
  // Das Formular steht, bevor die Dokumentdaten da sind — darum erst beim Eintreffen
  // aufklappen (nur auf, nie automatisch zu).
  useEffect(() => {
    if (hasOverrides) setOpen(true);
  }, [hasOverrides]);
  const labeled = squadLabeledKeys(keys, value);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)}>
        {open ? 'Bezeichnungen ausblenden' : 'Bezeichnungen anpassen'}
      </Button>
      {open ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Hochgestellte Ziffern für Fußnoten (¹ ² ³) direkt eintippen oder einfügen. Den Standardtext wieder
            eintragen stellt die Vorgabe her; leer lassen zeigt die Zeile ohne Bezeichnung.
          </Typography>
          {keys.map(({ key, label }, i) => (
            <TextField
              key={key}
              size="small"
              fullWidth
              label={`Standard: ${label}`}
              value={labeled[i].label}
              onChange={(e) => onChange(setSquadLabelOverride(value, keys, key, e.target.value))}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
};

const MapFieldsEditor: FC<{
  keys: Array<{ key: string; label: string }>;
  value: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  /** When set, money keys get Dynamic toggle + CellConfig. */
  enableDynamicMoneyFields?: boolean;
  isin?: string;
  /** Values that hold several lines (company plus address) — see squadFondspartner. */
  multiline?: boolean;
  /** Rows a multi-line field starts at; 1 keeps a table of short values compact. */
  minRows?: number;
  /** Keys that must stay on one line — a break in an identifier is never intended. */
  singleLineKeys?: string[];
  /** Rows shown but not editable here, with the value and the reason (e.g. ISIN → Fonds-ISIN). */
  readOnlyRows?: Record<string, { value: string; helperText: string }>;
  /** Further rows with a Dynamic switch (no CellConfig), and the config the switch sets. */
  dynamicRows?: { keys: readonly string[]; defaultFor: (key: string) => SquadDynamicCell };
}> = ({
  keys,
  value,
  onChange,
  enableDynamicMoneyFields = false,
  isin = '',
  multiline = false,
  minRows = 3,
  singleLineKeys,
  readOnlyRows,
  dynamicRows,
}) => {
  const theme = useTheme();
  const [cellConfigKey, setCellConfigKey] = useState<SquadDynamicFondsdatenKey | null>(null);

  return (
    <Stack spacing={1}>
      {keys.map(({ key, label }) => {
        const readOnly = readOnlyRows?.[key];
        if (readOnly) {
          return (
            <TextField
              key={key}
              size="small"
              fullWidth
              disabled
              label={label}
              value={readOnly.value}
              helperText={readOnly.helperText}
            />
          );
        }
        const configurable = enableDynamicMoneyFields && isSquadDynamicFondsdatenKey(key);
        const allowDynamic = configurable || Boolean(dynamicRows?.keys.includes(key));
        const raw = value?.[key];
        const isDynamic = allowDynamic && isSquadDynamicCell(raw);
        // Manual values are shown and stored as typed; formatting them per keystroke made typing impossible
        const display = isDynamic ? '' : String(raw ?? '');

        if (!allowDynamic) {
          const allowBreaks = multiline && !singleLineKeys?.includes(key);
          return (
            <TextField
              key={key}
              size="small"
              fullWidth
              multiline={allowBreaks}
              minRows={allowBreaks ? minRows : undefined}
              label={label}
              value={String(raw ?? '')}
              onChange={(e) => onChange({ ...value, [key]: e.target.value })}
            />
          );
        }

        return (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isDynamic ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      ...configWrapperStyles,
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    {(raw.fields || []).map((fieldConfig: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          ...configFieldStyles(theme, fieldConfig.type as 'table' | 'text' | 'html'),
                          maxWidth: '100%',
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {fieldConfig.type === 'text'
                            ? fieldConfig.text
                            : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  multiline={multiline && !singleLineKeys?.includes(key)}
                  minRows={multiline && !singleLineKeys?.includes(key) ? minRows : undefined}
                  label={label}
                  value={display}
                  onChange={(e) => onChange({ ...value, [key]: e.target.value })}
                />
              )}
            </Box>
            {isDynamic && configurable && (
              <IconButton
                size="small"
                onClick={() => setCellConfigKey(key)}
                sx={{ width: 32, height: 32 }}
                aria-label={`Configure ${label}`}
              >
                <Settings fontSize="small" />
              </IconButton>
            )}
            <Tooltip title="Toggle dynamic value (Axion)">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(isDynamic)}
                    color="secondary"
                    size="small"
                    onChange={() => {
                      if (isDynamic) {
                        onChange({ ...value, [key]: '' });
                      } else {
                        const cell = configurable ? defaultDynamicConfigForKey(key) : dynamicRows?.defaultFor(key);
                        if (cell) onChange({ ...value, [key]: cell });
                      }
                    }}
                  />
                }
                label="Dynamic"
                sx={{ m: 0, minWidth: 80 }}
              />
            </Tooltip>
          </Box>
        );
      })}

      {cellConfigKey && (
        <CellConfigDialog
          isin={isin}
          config={value?.[cellConfigKey]}
          open={Boolean(cellConfigKey)}
          onClose={() => setCellConfigKey(null)}
          onConfirm={(config) => {
            onChange({ ...value, [cellConfigKey]: config });
            setCellConfigKey(null);
          }}
        />
      )}
    </Stack>
  );
};

const iconSx = {
  width: 20,
  height: 20,
  cursor: 'pointer',
  transition: 'color 120ms ease',
  '&:hover': { color: 'secondary.dark' },
} as const;

/** Stable card wrapper — must live outside PdfConfig or every keystroke remounts the form (scroll jumps to top). */
const ConfigCard: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Stack spacing={1.5}>{children}</Stack>
    </CardContent>
  </Card>
);

/** Props for a group heading — groups and their order live in squadFormNav. */
const squadFormGroup = (key: SquadFormGroupKey) => {
  const group = SQUAD_FORM_GROUPS.find((g) => g.key === key);
  return {
    id: squadFormGroupId(key),
    title: group?.title ?? key,
    hint: group && 'hint' in group ? group.hint : undefined,
  };
};

/** Module scope for the same reason as ConfigCard: a component defined in PdfConfig remounts on every keystroke. */
const FormPageGroup: FC<{ id: string; title: string; hint?: string; children: React.ReactNode }> = ({
  id,
  title,
  hint,
  children,
}) => (
  <Box
    id={id}
    component="section"
    sx={{ display: 'flex', flexDirection: 'column', gap: 16, mt: 16, '&:first-of-type': { mt: 0 } }}
  >
    {/* CIXON CD: Überschrift in Purple, Linie im Verlauf Pink → Purple (Verlauf nur als Fläche) */}
    <Box sx={{ pb: 6, position: 'relative' }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: CIXON_CD.purple }}>{title}</Typography>
      {hint ? <Typography sx={{ fontSize: 12, color: CIXON_CD.middleGrey }}>{hint}</Typography> : null}
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: CIXON_CD.gradient }} />
    </Box>
    {children}
  </Box>
);

/** Scroll the form column so the group heading sits just below its top edge. */
const jumpToFormGroup = (key: SquadFormGroupKey) => {
  const target = window.document.getElementById(squadFormGroupId(key));
  if (target) scrollElementIntoScrollableColumn(target, 8);
};

/** Nearest ancestor that scrolls vertically — the form column. */
const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let parent = el?.parentElement || null;
  while (parent && parent !== window.document.body) {
    if (/(auto|scroll)/.test(window.getComputedStyle(parent).overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
};

type RailPlacement = { left: number; size: number; top: number };

/**
 * The free strip left of the form is the page's left padding (shared layout, not ours to
 * change): the first ancestor with a real padding-left. Measured before paint, so the
 * fallback button does not flash.
 */
const useFormRailPlacement = (formRef: React.RefObject<HTMLElement>): RailPlacement | null => {
  const [placement, setPlacement] = useState<RailPlacement | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      let strip: HTMLElement | null = formRef.current?.parentElement || null;
      while (strip && parseFloat(window.getComputedStyle(strip).paddingLeft) < 24) strip = strip.parentElement;
      const spot = strip
        ? squadRailPlacement(strip.getBoundingClientRect().left, parseFloat(window.getComputedStyle(strip).paddingLeft))
        : null;
      // Oberkante bündig mit dem Formularanfang (bei nicht gescrollter Spalte)
      const form = formRef.current;
      const scrolled = findScrollParent(form)?.scrollTop ?? 0;
      const next = spot && form ? { ...spot, top: Math.round(form.getBoundingClientRect().top + scrolled) } : null;
      setPlacement((prev) =>
        prev && next && prev.left === next.left && prev.size === next.size && prev.top === next.top ? prev : next,
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [formRef]);
  return placement;
};

/**
 * Jump rail in the free strip left of the form (05.10.2026): one circle per page — A, S1 …
 * S4, M. Fixed, because the form column clips anything that overhangs it; the circle of the
 * page in view is filled.
 */
const FormJumpRail: FC<{ formRef: React.RefObject<HTMLElement>; placement: RailPlacement }> = memo(({ formRef, placement }) => {
  const [active, setActive] = useState<string>(SQUAD_FORM_RAIL[0].short);
  // Nach einem Klick bleibt der angeklickte Kreis markiert, bis das Springen zu Ende ist —
  // sonst flackert die Markierung über die Seiten dazwischen.
  const clickLock = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdClick = () => {
    if (clickLock.current) clearTimeout(clickLock.current);
    clickLock.current = setTimeout(() => {
      clickLock.current = null;
    }, 250);
  };
  useEffect(() => {
    const scroller = findScrollParent(formRef.current);
    if (!scroller) return undefined;
    const onScroll = () => {
      if (clickLock.current) {
        holdClick();
        return;
      }
      const box = scroller.getBoundingClientRect();
      const toBottom = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
      const tops = SQUAD_FORM_GROUPS.map((g) => ({
        key: g.key,
        top: window.document.getElementById(squadFormGroupId(g.key))?.getBoundingClientRect().top ?? Infinity,
      }));
      setActive(squadRailItemForGroup(squadActiveFormGroup(tops, squadSpyLine(box.top, box.bottom, toBottom))));
    };
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (clickLock.current) clearTimeout(clickLock.current);
    };
  }, [formRef]);

  return (
    <Box
      component="nav"
      aria-label="Zu Seite springen"
      sx={{
        position: 'fixed',
        left: placement.left,
        top: placement.top,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {SQUAD_FORM_RAIL.map((item) => {
        const isActive = item.short === active;
        return (
          <Tooltip key={item.short} title={item.title} placement="right">
            <Button
              aria-label={item.title}
              aria-current={isActive ? 'location' : undefined}
              onClick={() => {
                setActive(item.short);
                holdClick();
                jumpToFormGroup(item.target);
              }}
              sx={{
                width: placement.size,
                height: placement.size,
                minWidth: 0,
                p: 0,
                borderRadius: '50%',
                // CIXON CD: Kontur in Purple, aktiv und beim Hover flächig Pink (Benjamin, 06.10.: kein Verlauf)
                border: '2px solid',
                borderColor: isActive ? CIXON_CD.pink : CIXON_CD.purple,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
                color: isActive ? '#fff' : CIXON_CD.purple,
                background: isActive ? CIXON_CD.pink : '#fff',
                transition: 'transform 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
                '&:hover': {
                  background: CIXON_CD.pink,
                  borderColor: CIXON_CD.pink,
                  color: '#fff',
                  transform: 'scale(1.08)',
                  boxShadow: '0 2px 8px rgba(203, 7, 116, 0.35)',
                },
                '&:focus-visible': { outline: `2px solid ${CIXON_CD.pink}`, outlineOffset: 2 },
              }}
            >
              {item.short}
            </Button>
          </Tooltip>
        );
      })}
    </Box>
  );
});
FormJumpRail.displayName = 'FormJumpRail';

/**
 * Small floating button at the bottom right of the form column that opens the list of
 * groups. Only shown when the strip beside the form is too narrow for the rail.
 * Sticky at the very end of the form, so it stays in view all the way down.
 */
const FormJumpMenu: FC = () => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <Box sx={{ position: 'sticky', bottom: 16, alignSelf: 'flex-end', zIndex: 2 }}>
      <Fab
        variant="extended"
        size="small"
        color="secondary"
        // The theme pins small Fabs to 36 × 36; the label needs its own width
        sx={{ width: 'auto', px: 14 }}
        aria-label="Zu Seite springen"
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <Toc fontSize="small" sx={{ mr: 6 }} />
        Seiten
      </Fab>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {SQUAD_FORM_GROUPS.map((g) => (
          <MenuItem
            key={g.key}
            dense
            onClick={() => {
              setAnchor(null);
              jumpToFormGroup(g.key);
            }}
          >
            {g.title}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/** Fields that must reach the parent/preview immediately (uploads / portfolio fetch), not typing. */
const isImmediateSyncField = (field: string) =>
  /ImageUrl$/i.test(field) || field === 'customPortfolioIsins';

/** Normalize stored dates for `<input type="date" />` (YYYY-MM-DD). */
function toDateInputValue(value?: string) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const de = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (de) {
    const [, dd, mm, yyyy] = de;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
  return iso.slice(0, 10);
}

export const PdfConfig: FC<PdfConfigProps> = ({ data, onChange, onRegisterFlush }) => {
  const theme = useTheme();
  const { document } = useContext(AppContext);
  const [formData, setFormData] = useState(data || {});
  const [editingHeader, setEditingHeader] = useState<{ key: SquadSectionHeaderKey; value: string } | null>(
    null
  );
  const [sectionHeaderCellConfigKey, setSectionHeaderCellConfigKey] =
    useState<SquadSectionHeaderKey | null>(null);
  const [kalenderjahrDialogOpen, setKalenderjahrDialogOpen] = useState(false);
  const [performanceRiskDialogOpen, setPerformanceRiskDialogOpen] = useState(false);
  const [portfolioIsinsInput, setPortfolioIsinsInput] = useState('');
  const [portfolioLoadState, setPortfolioLoadState] = useState<{
    loading: boolean;
    error?: string;
    name?: string;
  }>({ loading: false });
  const formDataRef = useRef(formData);
  const parentSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portfolioFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedIsinsKeyRef = useRef('');
  /** Parent-only runtime fields PdfConfig must not wipe on sync (history chart, captures). */
  const runtimeFieldsRef = useRef<Record<string, any>>({});

  const syncRuntimeFieldsFromParent = useCallback((parent: any) => {
    if (!parent || typeof parent !== 'object') return;
    const keys = [
      'lineChartData',
      'chartImageDataUrl',
      'chart1ImageDataUrl',
      'chart2ImageDataUrl',
      'chart3ImageDataUrl',
      'pieChart1ImageDataUrl',
      'pieChart2ImageDataUrl',
      'pieChart3ImageDataUrl',
    ] as const;
    const next = { ...runtimeFieldsRef.current };
    for (const key of keys) {
      if (key in parent) next[key] = parent[key];
    }
    runtimeFieldsRef.current = next;
  }, []);

  const mergeRuntimeInto = useCallback((updated: any) => {
    return { ...updated, ...runtimeFieldsRef.current };
  }, []);

  const emitParentChange = useCallback(
    (updated: any) => {
      const next = mergeRuntimeInto(updated);
      formDataRef.current = next;
      onChange(next);
      return next;
    },
    [mergeRuntimeInto, onChange],
  );

  const clearParentSyncTimer = useCallback(() => {
    if (parentSyncTimerRef.current) {
      clearTimeout(parentSyncTimerRef.current);
      parentSyncTimerRef.current = null;
    }
  }, []);

  const flushPendingParentSync = useCallback(() => {
    if (!parentSyncTimerRef.current) return;
    clearParentSyncTimer();
    emitParentChange(formDataRef.current);
  }, [clearParentSyncTimer, emitParentChange]);

  const scheduleParentSync = useCallback(() => {
    clearParentSyncTimer();
    parentSyncTimerRef.current = setTimeout(() => {
      parentSyncTimerRef.current = null;
      emitParentChange(formDataRef.current);
    }, PDF_CONFIG_PARENT_DEBOUNCE_MS);
  }, [clearParentSyncTimer, emitParentChange]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    syncRuntimeFieldsFromParent(data);
  }, [data, syncRuntimeFieldsFromParent]);

  useEffect(() => {
    if (!onRegisterFlush) return;
    onRegisterFlush(() => flushPendingParentSync());
  }, [onRegisterFlush, flushPendingParentSync]);

  useEffect(() => () => clearParentSyncTimer(), [clearParentSyncTimer]);

  useEffect(
    () => () => {
      if (portfolioFetchTimerRef.current) clearTimeout(portfolioFetchTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const isins = Array.isArray(data?.customPortfolioIsins) ? data.customPortfolioIsins : [];
    setPortfolioIsinsInput(isins.join(', '));
  }, [data?.customPortfolioIsins]);

  // Always load Risiko Indikator from Axion `cixon_stammdaten_extension.srriwert` (no manual overwrite).
  // Wait until form is hydrated from the parent document — otherwise a fast SRRI response can
  // scheduleParentSync({ riskIndicator }) while formData is still {}, then the pending-sync
  // guard blocks applying the real payload and the 400ms emit wipes the factsheet on F5.
  useEffect(() => {
    if (!isSquadConfigHydrated(formData)) return;

    let cancelled = false;
    const isin = resolveSquadFundIsin(formData);
    const until = formData?.published_at || formData?.historyUntil;
    (async () => {
      const level = await fetchSquadSrriwert(isin, until);
      if (cancelled || level == null) return;
      setFormData((prev: any) => {
        if (!isSquadConfigHydrated(prev)) return prev;
        if (Number(prev?.riskIndicator) === level) return prev;
        const updated = { ...prev, riskIndicator: level };
        formDataRef.current = updated;
        scheduleParentSync();
        return updated;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [
    formData?.title,
    formData?.historyIsin,
    formData?.fondsdaten?.isin,
    formData?.published_at,
    formData?.historyUntil,
    formData?.anlagestrategie,
    formData?.monatskommentar,
    scheduleParentSync,
  ]);

  useEffect(() => {
    if (!data) {
      setFormData({});
      formDataRef.current = {};
      return;
    }
    // While local edits are pending parent sync, ignore echo-backs / chart captures
    // so controlled inputs keep focus and the left pane does not remount mid-typing.
    // Exception: allow full document hydration to win over a sparse pre-hydration sync
    // (e.g. SRRI wrote { riskIndicator } before mapV1→V2 data arrived).
    if (parentSyncTimerRef.current) {
      if (isSquadConfigHydrated(data) && !isSquadConfigHydrated(formDataRef.current)) {
        clearParentSyncTimer();
        syncRuntimeFieldsFromParent(data);
        formDataRef.current = data;
        setFormData(data);
        return;
      }
      syncRuntimeFieldsFromParent(data);
      return;
    }
    // Ignore parent updates that only change ephemeral chart capture URLs —
    // those must not reset in-progress config (e.g. a just-uploaded image).
    const stripEphemeral = (d: any) => {
      if (!d || typeof d !== 'object') return d;
      const {
        chartImageDataUrl: _a,
        chart1ImageDataUrl: _b,
        chart2ImageDataUrl: _c,
        chart3ImageDataUrl: _d,
        pieChart1ImageDataUrl: _e,
        pieChart2ImageDataUrl: _f,
        pieChart3ImageDataUrl: _g,
        lineChartData: _h,
        ...rest
      } = d;
      // keep eslint happy (we only need to drop those keys)
      void _a;
      void _b;
      void _c;
      void _d;
      void _e;
      void _f;
      void _g;
      void _h;
      return rest;
    };
    try {
      if (JSON.stringify(stripEphemeral(data)) === JSON.stringify(stripEphemeral(formDataRef.current))) {
        syncRuntimeFieldsFromParent(data);
        return;
      }
    } catch {
      // ignore
    }
    formDataRef.current = data;
    setFormData(data);
  }, [data, syncRuntimeFieldsFromParent, clearParentSyncTimer]);

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev: any) => {
        const updated = { ...prev, [field]: value };
        formDataRef.current = updated;
        if (isImmediateSyncField(field)) {
          flushPendingParentSync();
          clearParentSyncTimer();
          emitParentChange(updated);
        } else {
          scheduleParentSync();
        }
        return updated;
      });
    },
    [scheduleParentSync, flushPendingParentSync, clearParentSyncTimer, emitParentChange]
  );

  /**
   * Titel|Farbtheme, zu dem die Header-Farben schon gesetzt sind. Der Portfolio-Snapshot
   * trägt seinen Schlüssel ein, damit der Seed-Effekt die Excel-Farben (FS-Header) nicht
   * gleich wieder mit der festen Tabelle überschreibt — bei Aguja Bond kamen so die
   * Navy- statt der Excel-Farben an (Basistest 02.10.2026).
   */
  const fsHeaderSeedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // PdfConfig startet mit data=null; erst das geladene Dokument zählt als „Öffnen“.
    if (!isSquadConfigHydrated(formData)) return;
    const seedKey = `${formData.title || ''}|${formData.farbthemeFund || ''}`;
    const firstRun = fsHeaderSeedKeyRef.current === null;
    if (fsHeaderSeedKeyRef.current === seedKey) return;
    fsHeaderSeedKeyRef.current = seedKey;
    // Beim Öffnen: Farben aus einem geladenen Portfolio-Upload gelten (Excel gewinnt).
    if (firstRun && parseCustomPortfolioIsins(formDataRef.current?.customPortfolioIsins).length > 0) return;
    // Seed / refresh pickers from fund-name defaults only (do not pass brand* or
    // resolve would echo the previous picker and never adopt a new fund palette).
    const next = suggestSquadFsHeaderPalette({
      title: formData.title,
      farbthemeFund: formData.farbthemeFund,
    });
    const current = formDataRef.current || {};
    const samePrimary = String(current.brandPrimary || '').toLowerCase() === next.primary.toLowerCase();
    const sameSecondary = String(current.brandSecondary || '').toLowerCase() === next.secondary.toLowerCase();
    if (samePrimary && sameSecondary) return;
    handleFieldChange('brandPrimary', next.primary);
    handleFieldChange('brandSecondary', next.secondary);
  }, [formData.title, formData.farbthemeFund, handleFieldChange]);

  /** Fonds-ISIN: Fondsdaten row and rolling bars follow in the same update — see squadFundIsin. */
  const handleFundIsinChange = useCallback(
    (value: string) => {
      setFormData((prev: any) => {
        const updated = syncSquadFundIsin({ ...prev, historyIsin: value });
        formDataRef.current = updated;
        scheduleParentSync();
        return updated;
      });
    },
    [scheduleParentSync],
  );

  const handleObjectFieldChange = useCallback(
    (objectKey: string, field: string, value: any) => {
      setFormData((prev: any) => {
        const updatedData = {
          ...prev,
          [objectKey]: {
            ...(prev[objectKey] || {}),
            [field]: value,
          },
        };
        formDataRef.current = updatedData;
        scheduleParentSync();
        return updatedData;
      });
    },
    [scheduleParentSync]
  );

  const loadPortfolioFromIsins = useCallback(
    async (isins: string[], opts?: { force?: boolean }) => {
      const key = isins.join(',');
      if (!isins.length) {
        setPortfolioLoadState({ loading: false });
        lastFetchedIsinsKeyRef.current = '';
        return;
      }
      if (!opts?.force && key === lastFetchedIsinsKeyRef.current) return;

      setPortfolioLoadState({ loading: true });
      try {
        const { data: res } = await client.query({
          query: GET_PORTFOLIO_UPLOAD,
          variables: { isins },
          fetchPolicy: 'network-only',
        });
        const upload = res?.getPortfolioUpload;
        if (!upload) {
          setPortfolioLoadState({
            loading: false,
            error: 'No portfolio found for these ISINs.',
          });
          return;
        }
        const sections = parsePortfolioWidgetsData(upload.portfolioWidgetsData);
        const mapped = mapPortfolioUploadToSquadPage2(sections);
        if (!mapped) {
          setPortfolioLoadState({
            loading: false,
            error: 'Portfolio has no usable widget data.',
            name: upload.name || undefined,
          });
          return;
        }

        const theme = parsePortfolioThemeData(upload.themeData);
        const chartColors = resolveSquadPortfolioChartColors(
          sections,
          theme,
          String(upload.farbthemeFund || ''),
        );
        const fsHeaderColors = suggestSquadFsHeaderPalette({
          title: String(formDataRef.current?.title || upload.name || ''),
          farbthemeFund: String(upload.farbthemeFund || ''),
          theme,
        });

        lastFetchedIsinsKeyRef.current = key;
        setFormData((prev: any) => {
          const { sectionHeaders: portfolioHeaders, ...mappedFields } = mapped;
          const commentFromUpload =
            typeof upload.monatskommentar === 'string' ? upload.monatskommentar.trim() : '';
          const updated = {
            ...prev,
            ...mappedFields,
            // Bereiche in Excel-Reihenfolge; die festen Felder oben bleiben für ältere Stände.
            portfolioSections: buildSquadPortfolioSections(sections, theme, String(upload.farbthemeFund || '')),
            assetAllocationColors: chartColors.assetAllocationColors || [],
            marktkapitalisierungColors: chartColors.marktkapitalisierungColors || [],
            portfolioBarColor: chartColors.portfolioBarColor || '',
            farbthemeFund: chartColors.farbthemeFund || '',
            // FS-Header primary/secondary drive header chrome + rolling brutto/netto.
            brandPrimary: fsHeaderColors.primary,
            brandSecondary: fsHeaderColors.secondary,
            ...(commentFromUpload ? { monatskommentar: commentFromUpload } : {}),
            customPortfolioIsins: isins,
            sectionHeaders: {
              ...mergeSquadSectionHeaders(prev.sectionHeaders),
              ...(portfolioHeaders || {}),
            },
            // Force pie re-capture for PDF after data refresh
            pieChart1ImageDataUrl: '',
            pieChart2ImageDataUrl: '',
          };
          runtimeFieldsRef.current = {
            ...runtimeFieldsRef.current,
            pieChart1ImageDataUrl: '',
            pieChart2ImageDataUrl: '',
          };
          formDataRef.current = updated;
          fsHeaderSeedKeyRef.current = `${updated.title || ''}|${updated.farbthemeFund || ''}`;
          flushPendingParentSync();
          clearParentSyncTimer();
          emitParentChange(updated);
          return updated;
        });
        setPortfolioLoadState({
          loading: false,
          name: upload.name || undefined,
        });
      } catch (err: any) {
        setPortfolioLoadState({
          loading: false,
          error: err?.message || 'Failed to load portfolio.',
        });
      }
    },
    [flushPendingParentSync, clearParentSyncTimer, emitParentChange],
  );

  const handlePortfolioIsinsChange = useCallback(
    (raw: string) => {
      setPortfolioIsinsInput(raw);
      const isins = parseCustomPortfolioIsins(raw);
      handleFieldChange('customPortfolioIsins', isins);
      if (portfolioFetchTimerRef.current) clearTimeout(portfolioFetchTimerRef.current);
      portfolioFetchTimerRef.current = setTimeout(() => {
        portfolioFetchTimerRef.current = null;
        void loadPortfolioFromIsins(isins);
      }, 700);
    },
    [handleFieldChange, loadPortfolioFromIsins],
  );

  const rollingPerformance = {
    ...DEFAULT_ROLLING_PERFORMANCE,
    ...(formData.rollingPerformance || {}),
    legend: {
      ...DEFAULT_ROLLING_PERFORMANCE.legend,
      ...(formData.rollingPerformance?.legend || {}),
    },
  };

  const getKalenderjahrData = useCallback((): TableData => {
    return normalizeKalenderjahrData(formData.kalenderjahrPerformance);
  }, [formData.kalenderjahrPerformance]);

  const handleSaveKalenderjahrDialog = useCallback(
    (tableData: TableData | LegacyTableData) => {
      const newFormatData =
        'labels' in tableData
          ? tableData
          : {
              labels: tableData.headers,
              values: tableData.rows.map((row: any) => row.values),
              columns: new Array(tableData.headers.length).fill({}).map(() => ({ type: 'text' as const })),
            };
      handleFieldChange('kalenderjahrPerformance', normalizeKalenderjahrData(newFormatData));
      setKalenderjahrDialogOpen(false);
    },
    [handleFieldChange]
  );

  const getPerformanceRiskData = useCallback((): TableData => {
    return normalizePerformanceRiskTable(formData.performanceRiskTable);
  }, [formData.performanceRiskTable]);

  const handleSavePerformanceRiskDialog = useCallback(
    (tableData: TableData | LegacyTableData) => {
      const newFormatData =
        'labels' in tableData
          ? tableData
          : {
              labels: tableData.headers,
              values: tableData.rows.map((row: any) => row.values),
              columns: new Array(tableData.headers.length).fill({}).map(() => ({ type: 'text' as const })),
            };
      handleFieldChange('performanceRiskTable', normalizePerformanceRiskTable(newFormatData));
      setPerformanceRiskDialogOpen(false);
    },
    [handleFieldChange]
  );

  const mifidOverlay = useMemo(
    () => normalizeMifidOverlay(formData.mifidPerformanceOverlay),
    [formData.mifidPerformanceOverlay],
  );

  const patchMifidOverlay = useCallback(
    (patch: Partial<ReturnType<typeof normalizeMifidOverlay>>) => {
      handleFieldChange('mifidPerformanceOverlay', { ...mifidOverlay, ...patch });
    },
    [handleFieldChange, mifidOverlay],
  );
  const mifidAreaKeys = Object.keys(MIFID_OVERLAY_AREA_LABELS) as SquadMifidOverlayAreaKey[];
  const mifidActiveCount = mifidAreaKeys.filter((key) => mifidOverlay[key]).length;
  // Zugeklappt, solange nichts abgedeckt ist; mit aktiven Bereichen beim Laden offen.
  const [mifidOpen, setMifidOpen] = useState(() => mifidActiveCount > 0);
  useEffect(() => {
    if (mifidActiveCount > 0) setMifidOpen(true);
  }, [mifidActiveCount]);

  // The three old page-3 slots: open while they are what gets printed, tucked away once the
  // combined image replaces them. Kept, not cleared — removing that image brings them back.
  const page3Legacy = resolveSquadPage3Layout(formData).kind === 'legacy';
  const page3HasLegacyImages = SQUAD_PAGE3_LEGACY_IMAGE_KEYS.some((key) => String(formData[key] || '').trim());
  const [page3LegacyOpen, setPage3LegacyOpen] = useState(() => page3Legacy);
  useEffect(() => {
    setPage3LegacyOpen(page3Legacy);
  }, [page3Legacy]);
  // Room for the combined image, from what the form holds — the header follows the title
  // size the PDF will use. The PDF preview measures the real thing.
  const page3TitleFontSize = useSquadTitleFontSize(String(formData.title || ''));
  const page3ImageArea = useMemo(
    () =>
      estimateSquadPage3ImageArea(
        {
          kalenderjahrPerformance: formData.kalenderjahrPerformance,
          kalenderjahrFootnote: formData.kalenderjahrFootnote,
          rollingPerformanceFootnote: formData.rollingPerformanceFootnote,
          page3FooterNote: formData.page3FooterNote,
        },
        page3TitleFontSize,
      ),
    [
      formData.kalenderjahrPerformance,
      formData.kalenderjahrFootnote,
      formData.rollingPerformanceFootnote,
      formData.page3FooterNote,
      page3TitleFontSize,
    ],
  );

  const formRef = useRef<HTMLDivElement>(null);
  const railPlacement = useFormRailPlacement(formRef);

  const sectionHeaders: SectionHeaders = mergeSquadSectionHeaders(formData.sectionHeaders);

  const displaySectionHeaders = useMemo(
    () => expandSquadSectionHeaders(formData.sectionHeaders, resolveSquadHeaderAsOf(formData)),
    [
      formData.sectionHeaders,
      formData.preise?.kursper,
      formData.data_as_of,
      formData.historyUntil,
      formData.published_at,
    ],
  );

  const handleScrollTo = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (!element) return;
    scrollElementIntoScrollableColumn(element);
  };

  const handleSectionHeaderChange = useCallback(
    (key: SquadSectionHeaderKey, value: SquadSectionHeaderValue) => {
      flushPendingParentSync();
      setFormData((prev: any) => {
        const updatedHeaders: SectionHeaders = {
          ...mergeSquadSectionHeaders(prev.sectionHeaders),
          [key]: value,
        };
        const updatedData = { ...prev, sectionHeaders: updatedHeaders };
        formDataRef.current = updatedData;
        emitParentChange(updatedData);
        return updatedData;
      });
      setEditingHeader(null);
    },
    [emitParentChange, flushPendingParentSync]
  );

  const handleConfirmSectionHeaderEdit = useCallback(() => {
    if (!editingHeader) return;
    handleSectionHeaderChange(editingHeader.key, editingHeader.value);
  }, [editingHeader, handleSectionHeaderChange]);

  /** Footnotes at the foot of one sidebar page — free text, see squadFootnotes. */
  const renderSidebarFootnotesCard = (page: SquadSidebarFootnotePage) => {
    const key = squadSidebarFootnoteKey(page);
    return (
      <ConfigCard>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Fußnoten Seite {page} (Seitenleiste unten)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Freitext, eine Zeile je Fußnote — so, wie sie im PDF stehen soll. Nummern selbst setzen (z. B. „1 – …“)
          oder weglassen. Hochgestellte Ziffern (¹ ² ³) gehören in den Inhalt, etwa in Überschriften oder
          Tabellenbezeichnungen.
        </Typography>
        <StringListEditor
          // Freitext: nur an Zeilenumbrüchen trennen — parseMultilineList würde einen
          // führenden Strich („– Quelle …“) als Aufzählungszeichen entfernen.
          items={formData[key] ? String(formData[key]).split('\n') : []}
          onChange={(items) => handleFieldChange(key, joinMultilineList(items))}
          addLabel="Fußnote hinzufügen"
          itemLabel="Zeile"
        />
      </ConfigCard>
    );
  };

  const renderSectionHeaderControls = (
    key: SquadSectionHeaderKey,
    options: { variant?: 'subtitle1' | 'subtitle2'; sectionId?: string; fontWeight?: number | string } = {}
  ) => {
    const { variant = 'subtitle1', sectionId, fontWeight = 'bold' } = options;
    const isEditing = editingHeader?.key === key;
    const rawValue = sectionHeaders[key];
    const isDynamic = isSquadDynamicCell(rawValue);
    const supportsDynamic = isDynamicSectionHeaderKey(key);
    const displayText = displaySectionHeaders[key] ?? resolveSquadSectionHeaderText(rawValue, resolveSquadHeaderAsOf(formData));

    if (supportsDynamic && isDynamic) {
      return (
        <Box sx={{ mb: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                ...configWrapperStyles,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {(rawValue.fields || []).map((fieldConfig: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    ...configFieldStyles(theme, fieldConfig.type as 'table' | 'text' | 'html'),
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                  onClick={() => setSectionHeaderCellConfigKey(key)}
                >
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {fieldConfig.type === 'text'
                      ? fieldConfig.text
                      : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Settings
              fontSize="small"
              color="secondary"
              onClick={() => setSectionHeaderCellConfigKey(key)}
              sx={iconSx}
              aria-label="Configure section header"
            />
            {sectionId ? (
              <OpenInNew onClick={() => handleScrollTo(sectionId)} color="secondary" sx={iconSx} />
            ) : null}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Preview: {displayText}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {isEditing ? (
            <>
              <TextField
                value={editingHeader?.value || ''}
                onChange={(e) => setEditingHeader((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
                size="small"
                autoFocus
                fullWidth
                InputProps={{ sx: { fontWeight } }}
              />
              <Check
                fontSize="small"
                color="secondary"
                onClick={handleConfirmSectionHeaderEdit}
                sx={iconSx}
              />
              <Close
                fontSize="small"
                color="secondary"
                onClick={() => setEditingHeader(null)}
                sx={iconSx}
              />
            </>
          ) : (
            <>
              <Typography variant={variant} fontWeight={fontWeight} sx={{ flex: 1 }}>
                {displayText}
              </Typography>
              <Edit
                fontSize="small"
                color="secondary"
                onClick={() =>
                  setEditingHeader({
                    key,
                    value: typeof rawValue === 'string' ? rawValue : displayText,
                  })
                }
                sx={iconSx}
              />
            </>
          )}
          {sectionId ? (
            <OpenInNew onClick={() => handleScrollTo(sectionId)} color="secondary" sx={iconSx} />
          ) : null}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      ref={formRef}
      sx={{ p: 2, mt: 0, mb: 64, display: 'flex', flexDirection: 'column', gap: 16 }}
      onBlur={(e) => {
        // Flush pending preview sync when focus leaves the config column
        // (keeps Save/Preview current without waiting for debounce).
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) return;
        flushPendingParentSync();
      }}
    >
      <Typography variant="h6" sx={{ color: CIXON_CD.darkGrey, lineHeight: 1.2 }}>
        PDF Configuration
      </Typography>

      {railPlacement ? <FormJumpRail formRef={formRef} placement={railPlacement} /> : null}

      <FormPageGroup {...squadFormGroup('allgemein')}>
        {/* Was jeden Monat geändert wird, ganz oben — ohne Scrollen (Weekly 02.10.2026) */}
        <ConfigCard>
          <Typography variant="h6" sx={{ mb: 1, fontSize: 16, fontWeight: 700, color: SQUAD_COLORS.navy }}>
            Monatliches Update
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Published at"
            type="date"
            value={toDateInputValue(formData.published_at)}
            onChange={(e) => handleFieldChange('published_at', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Customer Portfolio ISINs"
            placeholder="D11, D22, D33"
            value={portfolioIsinsInput}
            onChange={(e) => handlePortfolioIsinsChange(e.target.value)}
            helperText="Enter ISINs separated by commas. Loads page-2 allocations and Monatskommentar (Kommentar sheet A5) from Portfolio Upload."
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={portfolioLoadState.loading || !parseCustomPortfolioIsins(portfolioIsinsInput).length}
              onClick={() =>
                void loadPortfolioFromIsins(parseCustomPortfolioIsins(portfolioIsinsInput), { force: true })
              }
            >
              Reload from portfolio
            </Button>
            {portfolioLoadState.loading && <CircularProgress size={18} />}
            {portfolioLoadState.name && !portfolioLoadState.loading && (
              <Typography variant="caption" color="text.secondary">
                Loaded: {portfolioLoadState.name}
              </Typography>
            )}
            {portfolioLoadState.error && (
              <Typography variant="caption" color="error">
                {portfolioLoadState.error}
              </Typography>
            )}
          </Box>
          {parseCustomPortfolioIsins(portfolioIsinsInput).length > 0 && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Selected ISIN group:{' '}
                <strong>{parseCustomPortfolioIsins(portfolioIsinsInput).join(', ')}</strong>
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Bars, pies, tables, and section titles on page 2 (right) are filled from this portfolio.
          </Typography>
        </ConfigCard>

        <ConfigCard>
          <Typography variant="subtitle1" fontWeight="bold">
            Header Configuration
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Fund title"
            value={formData.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Fonds-ISIN"
            helperText="Steuert NAV-Chart, Risikoindikator (SRRI) und alle dynamischen Werte."
            value={formData.historyIsin || ''}
            onChange={(e) => handleFundIsinChange(e.target.value.trim().toUpperCase())}
          />
          <TextField
            fullWidth
            size="small"
            label="PDF file name (without .pdf)"
            placeholder="e.g. FS_SQUAD_Value_B_{date}"
            helperText="Use {date} to insert the as-of date (YYYYMMDD). Leave empty for auto-generated name."
            value={formData.pdf_download_name || ''}
            onChange={(e) => handleFieldChange('pdf_download_name', e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box>
              <Typography variant="caption">FS-Header primary (brutto)</Typography>
              <ColorPicker
                color={formData.brandPrimary || SQUAD_COLORS.navy}
                onChange={(c: string) => handleFieldChange('brandPrimary', c)}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    border: '1px solid #ddd',
                    backgroundColor: formData.brandPrimary || SQUAD_COLORS.navy,
                    cursor: 'pointer',
                  }}
                />
              </ColorPicker>
            </Box>
            <Box>
              <Typography variant="caption">FS-Header secondary (netto)</Typography>
              <ColorPicker
                color={formData.brandSecondary || SQUAD_FS_HEADER_SECONDARY}
                onChange={(c: string) => handleFieldChange('brandSecondary', c)}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    border: '1px solid #ddd',
                    backgroundColor: formData.brandSecondary || SQUAD_FS_HEADER_SECONDARY,
                    cursor: 'pointer',
                  }}
                />
              </ColorPicker>
            </Box>
          </Box>
        </ConfigCard>

      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s1-links')}>
        <ConfigCard>
          {renderSectionHeaderControls('fondsdaten', { sectionId: 'squad-fondsdaten-section' })}
          <MapFieldsEditor
            keys={squadLabeledKeys(SQUAD_FONDSDATEN_KEYS, formData.fondsdatenLabels)}
            value={formData.fondsdaten || {}}
            onChange={(v) => handleFieldChange('fondsdaten', v)}
            multiline
            minRows={1}
            singleLineKeys={SQUAD_SINGLE_LINE_FONDSDATEN_KEYS}
            enableDynamicMoneyFields
            isin={resolveSquadFundIsin(formData, document?.template)}
            readOnlyRows={{
              isin: {
                value: resolveSquadFundIsin(formData, document?.template),
                helperText: 'Folgt der Fonds-ISIN unter „Allgemein“.',
              },
            }}
          />
          <LabelOverridesEditor
            keys={SQUAD_FONDSDATEN_KEYS}
            value={formData.fondsdatenLabels}
            onChange={(v) => handleFieldChange('fondsdatenLabels', v)}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('fondsberater', { sectionId: 'squad-fondsberater-section' })}
          <AdvisorsEditor
            advisors={formData.advisors || []}
            onChange={(v) => handleFieldChange('advisors', normalizeSquadAdvisors(v))}
            uploadField={ImageUploadField}
            onUploadPhoto={(_index, url) => {
              const current = normalizeSquadAdvisors(formData.advisors || [])[0] || {
                name: '',
                photoUrl: '',
              };
              handleFieldChange('advisors', normalizeSquadAdvisors([{ ...current, photoUrl: url }]));
            }}
            onUploadLogo={(_index, url) => {
              const current = normalizeSquadAdvisors(formData.advisors || [])[0] || {
                name: '',
                logoUrl: '',
              };
              handleFieldChange('advisors', normalizeSquadAdvisors([{ ...current, logoUrl: url }]));
            }}
          />
        </ConfigCard>

        <ConfigCard>
          <ImageUploadField
            label={IMAGE_SLOT_LABELS.advisorLogoImage}
            inputId="squad-upload-advisor-logo"
            helperText={squadImageFormatHint(SQUAD_IMAGE_FORMATS.partnerLogo)}
            value={formData.advisorLogoImageUrl}
            onChange={(url) => handleFieldChange('advisorLogoImageUrl', url)}
            previewSx={{
              width: '100%',
              aspectRatio: `${SQUAD_IMAGE_FORMATS.partnerLogo.widthPx} / ${SQUAD_IMAGE_FORMATS.partnerLogo.heightPx}`,
              height: 'auto',
              maxHeight: 'none',
              objectFit: 'contain',
            }}
          />
        </ConfigCard>
        {renderSidebarFootnotesCard(1)}
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s1-rechts')}>
        <ConfigCard>
          {renderSectionHeaderControls('anlagestrategie', { sectionId: 'squad-anlagestrategie-section' })}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Content"
            value={formData.anlagestrategie || ''}
            onChange={(e) => handleFieldChange('anlagestrategie', e.target.value)}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('wertentwicklungSeitBeratung', {
            sectionId: 'squad-wertentwicklung-seit-beratung-section',
          })}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Chart footnote"
            helperText="Use Enter for line breaks"
            value={formData.chartFootnote || ''}
            onChange={(e) => handleFieldChange('chartFootnote', e.target.value)}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('wertentwicklungRisiko', {
            sectionId: 'squad-wertentwicklung-risiko-section',
          })}
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setPerformanceRiskDialogOpen(true)}
              size="small"
            >
              Configure Table
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Performance / risk footnote"
            helperText="Use Enter for line breaks"
            value={formData.performanceRiskFootnote || ''}
            onChange={(e) => handleFieldChange('performanceRiskFootnote', e.target.value)}
            sx={{ mt: 1.5 }}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('monatskommentar', { sectionId: 'squad-monatskommentar-section' })}
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Content"
            helperText="Use **bold** for emphasis. Auto-filled from Portfolio Upload (Kommentar A5) when Customer Portfolio ISINs are loaded. Die Länge steuert die Excel – läuft Seite 1 über, meldet das die PDF-Vorschau."
            value={formData.monatskommentar || ''}
            onChange={(e) => handleFieldChange('monatskommentar', e.target.value)}
          />
        </ConfigCard>

        <ConfigCard>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
            Seitenende: Siegel und Hinweis
          </Typography>
          <ImageUploadField
            label="Ratings / awards strip"
            inputId="squad-upload-ratings"
            value={formData.ratingsImageUrl}
            onChange={(url) => handleFieldChange('ratingsImageUrl', url)}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Hinweis unter den Siegeln"
            helperText="Use Enter for line breaks"
            value={formData.page1FooterNote || ''}
            onChange={(e) => handleFieldChange('page1FooterNote', e.target.value)}
          />
        </ConfigCard>
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s2-links')}>
        <ConfigCard>
          {renderSectionHeaderControls('weitereDaten', { sectionId: 'squad-weitere-daten-section' })}
          <MapFieldsEditor
            keys={squadLabeledKeys(SQUAD_WEITERE_DATEN_KEYS, formData.weitereDatenLabels)}
            value={formData.weitereDaten || {}}
            onChange={(v) => handleFieldChange('weitereDaten', v)}
            multiline
            minRows={1}
            dynamicRows={SQUAD_GEBUEHREN_DYNAMIC_ROWS}
          />
          <LabelOverridesEditor
            keys={SQUAD_WEITERE_DATEN_KEYS}
            value={formData.weitereDatenLabels}
            onChange={(v) => handleFieldChange('weitereDatenLabels', v)}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('fondspartner', { sectionId: 'squad-fondspartner-section' })}
          <MapFieldsEditor
            keys={SQUAD_FONDSPARTNER_KEYS}
            value={formData.fondspartner || {}}
            onChange={(v) => handleFieldChange('fondspartner', v)}
            multiline
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('kontakt', { sectionId: 'squad-kontakt-section' })}
          {(['company', 'address', 'phone', 'email', 'website'] as const).map((k) => (
            <TextField
              key={k}
              fullWidth
              size="small"
              multiline={k === 'address'}
              minRows={k === 'address' ? 2 : 1}
              label={k}
              value={formData.kontakt?.[k] || ''}
              onChange={(e) =>
                handleFieldChange('kontakt', { ...(formData.kontakt || {}), [k]: e.target.value })
              }
            />
          ))}
        </ConfigCard>

        <ConfigCard>
          <ImageUploadField
            label={IMAGE_SLOT_LABELS.fondspartnerLogoImage}
            inputId="squad-upload-fondspartner-logo"
            helperText={squadImageFormatHint(SQUAD_IMAGE_FORMATS.partnerLogo)}
            value={formData.fondspartnerLogoImageUrl}
            onChange={(url) => handleFieldChange('fondspartnerLogoImageUrl', url)}
            previewSx={{
              width: '100%',
              aspectRatio: `${SQUAD_IMAGE_FORMATS.partnerLogo.widthPx} / ${SQUAD_IMAGE_FORMATS.partnerLogo.heightPx}`,
              height: 'auto',
              maxHeight: 'none',
              objectFit: 'contain',
            }}
          />
        </ConfigCard>
        {renderSidebarFootnotesCard(2)}
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s2-rechts')}>
        {/* Page-2 right panel: one Custom Portfolio ISIN config (titles stay navy section headers) */}
        <ConfigCard>
          <Typography variant="h6" sx={{ mb: 1, fontSize: 16, fontWeight: 700, color: SQUAD_COLORS.navy }}>
            Darstellung Portfolio
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Die Portfolio-Daten (Customer Portfolio ISINs, „Reload from portfolio“) stehen oben unter „Allgemein“ → „Monatliches Update“.
          </Typography>
          {/* Kein Umschalter für die Tortendiagramme mehr: SQUAD hat E1 gewählt (15.10.2026), siehe normalizeSquadPieStyle. */}
        </ConfigCard>
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s3-links')}>
        <ConfigCard>
          {renderSectionHeaderControls('chancen', { sectionId: 'squad-chancen-section' })}
          <StringListEditor
            items={parseMultilineList(formData.chancen || '')}
            onChange={(items) => handleFieldChange('chancen', joinMultilineList(items))}
            addLabel="Add chance"
            itemLabel="Chance"
            marker="+"
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('risiken', { sectionId: 'squad-risiken-section' })}
          <StringListEditor
            items={parseMultilineList(formData.risiken || '')}
            onChange={(items) => handleFieldChange('risiken', joinMultilineList(items))}
            addLabel="Add risk"
            itemLabel="Risk"
            marker="-"
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Risiken footnote (italic)"
            value={formData.risikenNote || ''}
            onChange={(e) => handleFieldChange('risikenNote', e.target.value)}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('risikoIndikator', { sectionId: 'squad-risiko-indikator-section' })}
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Risiko Indikator (1–7)"
            value={formData.riskIndicator ?? 3}
            InputProps={{ readOnly: true }}
          />
          <Box
            sx={{
              ...configWrapperStyles,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {SQUAD_SRRIWERT_CELL_CONFIG.fields.map((fieldConfig, index) => (
              <Box
                key={index}
                sx={{
                  ...configFieldStyles(theme, fieldConfig.type as 'table' | 'text' | 'html'),
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  cursor: 'default',
                }}
              >
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {`${fieldConfig.table}.${fieldConfig.field}`}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            SRRI kommt aus Axion für die Fonds-ISIN unter „Allgemein“:{' '}
            <strong>{resolveSquadFundIsin(formData, document?.template)}</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Risiko Indikator text"
            value={formData.riskIndicatorText || ''}
            onChange={(e) => handleFieldChange('riskIndicatorText', e.target.value)}
          />
        </ConfigCard>
        {renderSidebarFootnotesCard(3)}
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s3-rechts')}>
        <ConfigCard>
          {renderSectionHeaderControls('kalenderjahr', { sectionId: 'squad-kalenderjahr-section' })}
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setKalenderjahrDialogOpen(true)}
              size="small"
            >
              Configure Table
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Kalenderjahr footnote"
            helperText="Use Enter for line breaks"
            value={formData.kalenderjahrFootnote || ''}
            onChange={(e) => handleFieldChange('kalenderjahrFootnote', e.target.value)}
            sx={{ mb: 1.5 }}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('rollierend', { sectionId: 'squad-rollierend-section' })}
          <Box sx={{ my: 1 }}>
            <Select
              value={rollingPerformance.chartType || 'Automated'}
              onChange={(e) => {
                flushPendingParentSync();
                const newChartType = e.target.value;
                const updatedData = {
                  ...formData,
                  rollingPerformance: {
                    ...rollingPerformance,
                    chartType: newChartType,
                    ...(newChartType === 'Automated' && { automationType: 'all' }),
                  },
                };
                formDataRef.current = updatedData;
                setFormData(updatedData);
                emitParentChange(updatedData);
              }}
              sx={{ minWidth: 150, mb: 1.5 }}
              size="small"
            >
              <MenuItem value="Manual">Manual</MenuItem>
              <MenuItem value="Automated">Automated</MenuItem>
            </Select>

            {rollingPerformance.chartType === 'Automated' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Tooltip title="Folgt der Fonds-ISIN unter „Allgemein“.">
                    <OutlinedInput
                      value={resolveSquadFundIsin(formData, document?.template)}
                      disabled
                      placeholder="ISIN"
                      size="small"
                      sx={{ width: 200 }}
                      inputProps={{ 'aria-label': 'ISIN der rollierenden Wertentwicklung (folgt der Fonds-ISIN)' }}
                    />
                  </Tooltip>
                  <Select
                    value={rollingPerformance.automationType || 'all'}
                    onChange={(e) =>
                      handleObjectFieldChange('rollingPerformance', 'automationType', e.target.value)
                    }
                    size="small"
                    sx={{ width: 200 }}
                  >
                    <MenuItem value="all">All (brutto + netto)</MenuItem>
                    <MenuItem value="gross_perf_year_as_of_today">Gross</MenuItem>
                    <MenuItem value="perf_year_as_of_today">Net</MenuItem>
                  </Select>
                </Box>
                {(rollingPerformance.automationType === 'all' ||
                  !rollingPerformance.automationType ||
                  rollingPerformance.automationType === 'gross_perf_year_as_of_today') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 48 }}>
                      Gross:
                    </Typography>
                    <OutlinedInput
                      value={rollingPerformance.legend?.gross_perf_year_as_of_today || ''}
                      onChange={(e) =>
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(rollingPerformance.legend || {}),
                          gross_perf_year_as_of_today: e.target.value,
                        })
                      }
                      placeholder="e.g., brutto"
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
                {(rollingPerformance.automationType === 'all' ||
                  rollingPerformance.automationType === 'perf_year_as_of_today') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 48 }}>
                      Net:
                    </Typography>
                    <OutlinedInput
                      value={rollingPerformance.legend?.perf_year_as_of_today || ''}
                      onChange={(e) =>
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(rollingPerformance.legend || {}),
                          perf_year_as_of_today: e.target.value,
                        })
                      }
                      placeholder="e.g., netto (mit 5% Ausgabeaufschlag)"
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {rollingPerformance.chartType !== 'Automated' && (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} mt={1}>
                  <Typography variant="body2" color="text.secondary">
                    Manual periods (brutto / netto)
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const periods = [
                        ...(rollingPerformance.periods || []),
                        { label: '', brutto: 0, netto: 0 },
                      ];
                      handleObjectFieldChange('rollingPerformance', 'periods', periods);
                    }}
                    aria-label="Add period"
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
                <Stack spacing={1}>
                  {(rollingPerformance.periods || []).map((period: any, index: number) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        label={`Period ${index + 1}`}
                        value={period.label || ''}
                        onChange={(e) => {
                          const periods = [...(rollingPerformance.periods || [])];
                          periods[index] = { ...period, label: e.target.value };
                          handleObjectFieldChange('rollingPerformance', 'periods', periods);
                        }}
                        size="small"
                        sx={{ flex: 1, minWidth: 180 }}
                      />
                      <TextField
                        label="Brutto %"
                        type="number"
                        value={period.brutto ?? period.value ?? ''}
                        onChange={(e) => {
                          const periods = [...(rollingPerformance.periods || [])];
                          periods[index] = { ...period, brutto: parseFloat(e.target.value) || 0 };
                          handleObjectFieldChange('rollingPerformance', 'periods', periods);
                        }}
                        size="small"
                        sx={{ width: 100 }}
                      />
                      <TextField
                        label="Netto %"
                        type="number"
                        value={period.netto ?? ''}
                        onChange={(e) => {
                          const periods = [...(rollingPerformance.periods || [])];
                          periods[index] = { ...period, netto: parseFloat(e.target.value) || 0 };
                          handleObjectFieldChange('rollingPerformance', 'periods', periods);
                        }}
                        size="small"
                        sx={{ width: 100 }}
                      />
                      <IconButton
                        onClick={() => {
                          const periods = [...(rollingPerformance.periods || [])];
                          periods.splice(index, 1);
                          handleObjectFieldChange('rollingPerformance', 'periods', periods);
                        }}
                        color="error"
                        size="small"
                        aria-label={`Remove period ${index + 1}`}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Rolling performance footnote"
            helperText="Use Enter for line breaks"
            value={formData.rollingPerformanceFootnote || ''}
            onChange={(e) => handleFieldChange('rollingPerformanceFootnote', e.target.value)}
            sx={{ mt: 1.5 }}
          />
        </ConfigCard>

        <ConfigCard>
          <Typography variant="subtitle1" fontWeight="bold">
            Bild unter „Rollierende Wertentwicklung“
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {squadPage3OptimalSizeText(page3ImageArea)}
          </Typography>
          <ImageUploadField
            label="Ein Bild für den Rest der Seite, mit den Überschriften im Bild"
            inputId="squad-upload-page3-image"
            helperText={SQUAD_PAGE3_IMAGE_HINT}
            value={formData.page3ImageUrl}
            onChange={(url) => handleFieldChange('page3ImageUrl', url)}
            previewSx={formData.page3ImageUrl ? { maxHeight: 240 } : undefined}
          />
          <SquadPage3ImageFitStatus area={page3ImageArea} imageUrl={formData.page3ImageUrl} />
        </ConfigCard>

        {page3HasLegacyImages || page3LegacyOpen ? (
          <ConfigCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <Box>
                <Typography variant="subtitle2">Bisherige drei Bilder</Typography>
                <Typography variant="caption" color="text.secondary">
                  {page3Legacy
                    ? 'Im PDF, bis oben ein Bild hochgeladen ist'
                    : page3HasLegacyImages
                      ? 'Nicht im PDF – oben ist ein Bild hochgeladen. Entfernen holt sie zurück.'
                      : 'Leer'}
                </Typography>
              </Box>
              <Button
                size="small"
                aria-expanded={page3LegacyOpen}
                aria-controls={page3LegacyOpen ? 'squad-page3-legacy-images' : undefined}
                endIcon={page3LegacyOpen ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setPage3LegacyOpen((open) => !open)}
                sx={{ flexShrink: 0 }}
              >
                {page3LegacyOpen ? 'Ausblenden' : 'Anzeigen'}
              </Button>
            </Box>
            {page3LegacyOpen ? (
              <Stack id="squad-page3-legacy-images" spacing={1.5}>
                {renderSectionHeaderControls('historischeAssetAllokation', {
                  sectionId: 'squad-historische-asset-allokation-section',
                })}
                <ImageUploadField
                  label="Image"
                  inputId="squad-upload-historische-asset-allokation"
                  value={formData.historischeAssetAllokationImageUrl}
                  onChange={(url) => handleFieldChange('historischeAssetAllokationImageUrl', url)}
                />
                {renderSectionHeaderControls('investmentprozess', { sectionId: 'squad-investmentprozess-section' })}
                <ImageUploadField
                  label="Image"
                  inputId="squad-upload-investmentprozess"
                  value={formData.investmentprozessImageUrl}
                  onChange={(url) => handleFieldChange('investmentprozessImageUrl', url)}
                />
                {renderSectionHeaderControls('zusammenfassung', { sectionId: 'squad-zusammenfassung-section' })}
                <ImageUploadField
                  label="Image"
                  inputId="squad-upload-zusammenfassung"
                  value={formData.zusammenfassungImageUrl}
                  onChange={(url) => handleFieldChange('zusammenfassungImageUrl', url)}
                />
              </Stack>
            ) : null}
          </ConfigCard>
        ) : null}

        <ConfigCard>
          <Typography variant="subtitle1" fontWeight="bold">
            Hinweis am Seitenende
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Page 3 footer note"
            helperText="Use Enter for line breaks"
            value={formData.page3FooterNote || ''}
            onChange={(e) => handleFieldChange('page3FooterNote', e.target.value)}
          />
        </ConfigCard>
      </FormPageGroup>

      <FormPageGroup {...squadFormGroup('s4')}>
        <ConfigCard>
          {renderSectionHeaderControls('erlaeuterungen', { sectionId: 'squad-erlaeuterungen-section' })}
          <SquadRichTextEditor
            value={formData.erlaeuterungen || ''}
            onChange={(html) => handleFieldChange('erlaeuterungen', html)}
            minHeight={140}
          />
        </ConfigCard>

        <ConfigCard>
          {renderSectionHeaderControls('wichtigeHinweise', { sectionId: 'squad-wichtige-hinweise-section' })}
          <SquadRichTextEditor
            value={formData.wichtigeHinweise || ''}
            onChange={(html) => handleFieldChange('wichtigeHinweise', html)}
            minHeight={200}
          />
        </ConfigCard>
      </FormPageGroup>

      {/* Optional und selten gebraucht — darum ganz unten und zugeklappt (05.10.2026) */}
      <FormPageGroup {...squadFormGroup('mifid')}>
        <ConfigCard>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Box>
              <Typography variant="subtitle2">MiFID performance overlay (under 12 months)</Typography>
              <Typography variant="caption" color="text.secondary">
                {mifidActiveCount > 0
                  ? `Aktiv: ${mifidActiveCount} von ${mifidAreaKeys.length} Bereichen abgedeckt`
                  : 'Aus — kein Bereich abgedeckt'}
              </Typography>
            </Box>
            <Button
              size="small"
              aria-expanded={mifidOpen}
              aria-controls={mifidOpen ? 'squad-mifid-settings' : undefined}
              endIcon={mifidOpen ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setMifidOpen((open) => !open)}
            >
              {mifidOpen ? 'Ausblenden' : 'Einstellungen'}
            </Button>
          </Box>
          {mifidOpen ? (
            <Stack id="squad-mifid-settings" spacing={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Cover performance areas with an opaque white layer and shared notice text. Navy/gold headings stay
                visible; page layout is unchanged. Each area can be switched independently.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Notice text (shared by all four areas)"
                value={mifidOverlay.noticeText}
                onChange={(e) => patchMifidOverlay({ noticeText: e.target.value })}
              />
              <Stack spacing={0.5}>
                {mifidAreaKeys.map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={mifidOverlay[key]}
                        onChange={(e) => patchMifidOverlay({ [key]: e.target.checked })}
                      />
                    }
                    label={MIFID_OVERLAY_AREA_LABELS[key]}
                  />
                ))}
              </Stack>
            </Stack>
          ) : null}
        </ConfigCard>
      </FormPageGroup>

      <TableConfigurationDialog
        open={performanceRiskDialogOpen}
        onClose={() => setPerformanceRiskDialogOpen(false)}
        onSave={handleSavePerformanceRiskDialog}
        initialData={getPerformanceRiskData()}
        title="Configure Wertentwicklung / Risikokennzahlen"
        isin={formData.historyIsin || formData.isin}
      />

      <TableConfigurationDialog
        open={kalenderjahrDialogOpen}
        onClose={() => setKalenderjahrDialogOpen(false)}
        onSave={handleSaveKalenderjahrDialog}
        initialData={getKalenderjahrData()}
        title="Configure Kalenderjahr Table"
        isin={formData.historyIsin || formData.isin}
      />

      {(() => {
        const headerKey = sectionHeaderCellConfigKey;
        const headerConfig = headerKey ? sectionHeaders[headerKey] : null;
        if (!headerKey || !isSquadDynamicCell(headerConfig)) return null;
        return (
          <CellConfigDialog
            isin={resolveSquadFundIsin(formData, document?.template)}
            config={headerConfig}
            open
            onClose={() => setSectionHeaderCellConfigKey(null)}
            onConfirm={(config) => {
              handleSectionHeaderChange(headerKey, config);
              setSectionHeaderCellConfigKey(null);
            }}
          />
        );
      })()}

      {/* Last child on purpose: sticky bottom keeps it in view down the whole form */}
      {railPlacement ? null : <FormJumpMenu />}
    </Box>
  );
};
